import cloneDeep from 'lodash/cloneDeep';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import applyDesignDataToOperation from 'editor/src/store/design/operation/applyDesignDataToOperation';
import setDesignDataOperation from 'editor/src/store/design/operation/setDesignDataOperation';
import setSpreadBackgroundsOperation from 'editor/src/store/design/operation/setSpreadBackgroundsOperation';
import setSpreadForegroundsOperation from 'editor/src/store/design/operation/setSpreadForegroundsOperation';
import { DesignData, SpreadGroundImage } from 'editor/src/store/design/types';
import getLayoutSchemaByName from 'editor/src/store/editor/selector/getLayoutSchemaByName';
import type { Thunk } from 'editor/src/store/hooks';
import { getDesignKeyFromDesign } from 'editor/src/store/variants/helpers/getDesignKey';
import getDriftedPageCountTemplate from 'editor/src/store/variants/helpers/getDriftedPageCountTemplate';
import {
  getGroupRepresentativeVariant,
  getVariantDesignKey,
} from 'editor/src/store/variants/helpers/getGroupDesignKey';
import getNewDesignDataForGroup, {
  getSelectedDesignOptionGroupKeys,
} from 'editor/src/store/variants/helpers/getNewDesignDataForGroup';
import getResizableProductForGivenVariant from 'editor/src/store/variants/selector/getResizableProductForGivenVariant';
import { setGroupDesignDataAction } from 'editor/src/store/variants/slice';

import applyResizableProductToDesign from 'editor/src/util/design/applyResizableProductToDesign';
import applyLayoutPerProductUid from 'editor/src/util/layouts/applyLayoutPerProductUid';
import reflectDesignData from 'editor/src/util/reflectDesignData';
import getReflectContext from 'editor/src/util/reflectDesignData/getReflectContext';

import sendProductSwitchedOperation from './sendProductSwitchedOperation';
import setupProductSizeOperation from './setupProductSizeOperation';

export type DesignTemplate = {
  designData: DesignData;
  spreadBackgrounds?: SpreadGroundImage[];
  spreadForegrounds?: SpreadGroundImage[];
};

// used to differientiate between different photobook designs where the spine grows with the page count
function areDesignCoverTheSameSize(d1: DesignData, d2: DesignData) {
  return (
    d1.spreads[0].pages[0].width === d2.spreads[0].pages[0].width &&
    d1.spreads[0].pages[0].height === d2.spreads[0].pages[0].height
  );
}

export function haveSameNumberOfSpreads(d1?: DesignData, d2?: DesignData) {
  return d1?.spreads.length === d2?.spreads.length;
}

const applyTemplatesIfNeededOperation =
  (templates?: DesignTemplate[]): Thunk =>
  (dispatch, getState, { i18n }) => {
    batch(() => {
      // Lay a drifted design onto its template before the groups below copy it.
      const driftedTemplate = getDriftedPageCountTemplate(getState());
      if (driftedTemplate) {
        dispatch(applyDesignDataToOperation({ design: driftedTemplate }));
      }

      const state = getState();
      const { selectedGroupKey, variationGroups, designTemplates, spreadImages, product, selectedPageCount } =
        state.variants;

      const sourceGroup = selectedGroupKey
        ? variationGroups.find((group) => group.key === selectedGroupKey)
        : variationGroups[0];
      const sourceDesignData =
        sourceGroup?.variationsInfo.find((variationInfo) => !!variationInfo.designData)?.designData ||
        state.design.designData;
      const reflectContext = getReflectContext(state);
      const selectedDesignOptionGroupKeys = getSelectedDesignOptionGroupKeys(state);

      variationGroups.forEach((group) => {
        const variationInfo = getGroupRepresentativeVariant(group);
        const designKey = getVariantDesignKey(variationInfo, selectedPageCount);
        const templateData = designTemplates[designKey];
        // GAPI-47583: a linked group must follow the ACTIVE design, exactly as
        // it does in `designListener` on every design edit. Re-reflecting the
        // group's own designData here re-asserted the group's PRE-EDIT design
        // as current whenever its templates arrived after a design edit (the
        // listener had skipped the group while the templates were in flight),
        // permanently exporting an out-of-date — typically empty — design to
        // the host. Only fall back to the group's own design when there is no
        // active design to follow, or when the group does not follow it
        // (unlinked, or belonging to a non-selected design option).
        const followsActiveDesign =
          !!state.design.designData &&
          group.linked &&
          (!selectedDesignOptionGroupKeys || selectedDesignOptionGroupKeys.includes(group.key));
        if (!variationInfo.designData && templateData) {
          // GAPI-39157: pick a source design whose spread count matches the
          // target template. Without this, adding a 1-spread variant while a
          // 2-spread group is selected uses the 2-spread design as source,
          // leaking content across spread-count groups. Fall back to the
          // global sourceDesignData only when no same-spread-count source
          // exists (preserves the original cross-spread positional fallback
          // for the "first variants added" case).
          const matchesSpreadCount = (v: { designData?: DesignData }) =>
            v.designData && v.designData.spreads.length === templateData.spreads.length;
          const sameSpreadSource = variationGroups
            .find((g) => g.variationsInfo.some(matchesSpreadCount))
            ?.variationsInfo.find(matchesSpreadCount)?.designData;
          const effectiveSource = sameSpreadSource || sourceDesignData;
          let designData = effectiveSource
            ? reflectDesignData(effectiveSource, templateData, reflectContext)
            : templateData;
          designData = applyLayoutPerProductUid(designData, state, !!sourceDesignData, i18n);

          const resizableElement = getResizableProductForGivenVariant(state, designData.product_uid);
          const layoutName = designData.spreads[0].layoutSchemaName;
          const layout = layoutName ? getLayoutSchemaByName(state, layoutName) : undefined;

          if (resizableElement && layout && designData.related_dimensions) {
            designData = applyResizableProductToDesign(state, designData, resizableElement, layout, i18n);
          }

          dispatch(
            setGroupDesignDataAction({
              groupKey: group.key,
              designData,
              reflectContext,
            }),
          );
        } else if (variationInfo.designData && followsActiveDesign) {
          const groupDesignUpdate = getNewDesignDataForGroup(group, state, i18n);
          dispatch(
            setGroupDesignDataAction({
              groupKey: group.key,
              designData: groupDesignUpdate.designData,
              reflectContext,
              templatesMissing: groupDesignUpdate.missingTemplate,
            }),
          );
        } else if (variationInfo.designData && templateData) {
          dispatch(
            setGroupDesignDataAction({
              groupKey: group.key,
              designData: reflectDesignData(variationInfo.designData, templateData, reflectContext),
              reflectContext,
            }),
          );
        }

        if (selectedGroupKey === group.key) {
          const spreadImage = spreadImages[designKey];
          if (spreadImage) {
            void dispatch(
              setSpreadBackgroundsOperation({
                productUid: variationInfo.variation.productUid,
                spreads: spreadImage.spreadBackgrounds,
              }),
            );
            void dispatch(
              setSpreadForegroundsOperation({
                productUid: variationInfo.variation.productUid,
                spreads: spreadImage.spreadForegrounds,
              }),
            );
          }
        }
      });

      if (variationGroups.length === 0 && templates?.length && product.externalProductControls.length) {
        const { designData } = templates[0];
        if (designData) {
          product.externalProductControls.forEach((control) => {
            const dimensions = designData.related_dimensions || designData.dimensions;
            if (control.key === 'product-size' && dimensions) {
              dispatch(setupProductSizeOperation(dimensions));
            }
          });
        }
      }

      const selectedGroup = getState().variants.variationGroups.find(
        (group) => !selectedGroupKey || group.key === selectedGroupKey,
      );
      const variantInfo = selectedGroup?.variationsInfo.find((group) => group.designData);

      if (
        variantInfo &&
        variantInfo.designData &&
        (!state.design.designData ||
          getDesignKeyFromDesign(state.design.designData) !== getDesignKeyFromDesign(variantInfo.designData) ||
          !areDesignCoverTheSameSize(state.design.designData, variantInfo.designData) ||
          !haveSameNumberOfSpreads(state.design.designData, variantInfo.designData))
      ) {
        void dispatch(setDesignDataOperation(cloneDeep(variantInfo.designData)));
        dispatch(sendProductSwitchedOperation(variantInfo));
        const images = spreadImages[getDesignKeyFromDesign(variantInfo.designData)];
        if (images?.spreadBackgrounds) {
          void dispatch(
            setSpreadBackgroundsOperation({
              productUid: variantInfo.designData.product_uid,
              spreads: images.spreadBackgrounds,
            }),
          );
        }
        if (images?.spreadForegrounds) {
          void dispatch(
            setSpreadForegroundsOperation({
              productUid: variantInfo.designData.product_uid,
              spreads: images.spreadForegrounds,
            }),
          );
        }
      }
    });
  };

export default applyTemplatesIfNeededOperation;
